import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import api from 'app/shared/utils/api';
import type { CostsAndFees } from 'app/types/costAndFees.type';

export interface ApiResponse {
  data: CostsAndFees;
  status: string;
  success: boolean;
  category: string;
}

const MAX_CACHE_SIZE = 50;
const CACHE_EXPIRY_MS = 15 * 60 * 1000;

// Cache structure with expiry timestamps
interface CacheEntry {
  data: CostsAndFees;
  timestamp: number;
}

const costAndFeesCache = new Map<string, CacheEntry>();

/**
 * Custom hook to fetch and cache cost and fees data for a listing
 * @param listingAlias - The unique identifier for the listing
 */
export const useCostAndFeesWithCache = (listingAlias: string) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [listingCostAndFees, setListingCostAndFees] = useState<CostsAndFees | null>(null);

  /**
   * Manages the cache size by removing the oldest entries when the cache exceeds MAX_CACHE_SIZE
   */
  const manageCacheSize = useCallback(() => {
    if (costAndFeesCache.size <= MAX_CACHE_SIZE) return;

    const entries = Array.from(costAndFeesCache.entries());
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);

    const entriesToRemove = entries.slice(0, entries.length - MAX_CACHE_SIZE);
    entriesToRemove.forEach(([key]) => costAndFeesCache.delete(key));
  }, []);

  /**
   * Fetches cost and fees data with caching
   * - Returns cached data if available and not expired
   * - Fetches fresh data from API if needed
   */
  const fetchCostAndFeesWithCache = useCallback(
    async (listingAlias: string) => {
      try {
        setError(null);

        const now = Date.now();
        const cachedEntry = costAndFeesCache.get(listingAlias);

        if (cachedEntry && now - cachedEntry.timestamp < CACHE_EXPIRY_MS) {
          setListingCostAndFees(cachedEntry.data);
          return cachedEntry.data;
        }

        setIsLoading(true);
        const response = await dispatch(api.listing.fetchCostAndFees(listingAlias));

        if (!response) {
          throw new Error('Empty response from API');
        }

        const { data } = response as unknown as ApiResponse;

        costAndFeesCache.set(listingAlias, {
          data,
          timestamp: now,
        });
        manageCacheSize();

        setListingCostAndFees(data);
        return data;
      } catch (err) {
        const errorObj = err instanceof Error ? err : new Error(`Failed to fetch cost and fees: ${String(err)}`);
        setListingCostAndFees(null);
        setError(errorObj);
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, manageCacheSize],
  );

  useEffect(() => {
    fetchCostAndFeesWithCache(listingAlias);
  }, [fetchCostAndFeesWithCache, listingAlias]);

  return {
    error,
    fetchCostAndFeesWithCache,
    isLoading,
    listingCostAndFees,
  };
};
export const __clearCache = () => {
  costAndFeesCache.clear();
};
